import { Link, useParams, useNavigate } from "react-router-dom";
import { useWork, useWorkExpressions } from "@/api/hooks";
import type { ExpressionSummary, RelatedWork, WorkCreator } from "@/api/types";
import { WorkRelationsGraph } from "../components";

// Work form labels in French
const WORK_FORM_LABELS: Record<string, string> = {
  novel: "Roman",
  short_story: "Nouvelle",
  poetry: "Poésie",
  theater: "Théâtre",
  essay: "Essai",
  biography: "Biographie",
  documentary: "Documentaire",
  film: "Film",
  series: "Série",
  animation: "Animation",
  symphony: "Symphonie",
  opera: "Opéra",
  concerto: "Concerto",
  song: "Chanson",
  album: "Album",
  painting: "Peinture",
  sculpture: "Sculpture",
  photograph: "Photographie",
  graphic_novel: "Bande dessinée",
  manga: "Manga",
  video_game: "Jeu vidéo",
  software: "Logiciel",
  website: "Site web",
  other: "Autre",
};

// Expression form labels
const EXPRESSION_FORM_LABELS: Record<string, string> = {
  original: "Original",
  translation: "Traduction",
  abridged: "Abrégé",
  annotated: "Annoté",
  critical: "Édition critique",
  illustrated: "Illustré",
  revised: "Révisé",
  dramatization: "Adaptation dramatique",
  novelization: "Novélisation",
  musical_arrangement: "Arrangement musical",
  performance: "Interprétation",
  dubbed: "Doublé",
  subtitled: "Sous-titré",
  other: "Autre",
};

// Language labels
const LANGUAGE_LABELS: Record<string, string> = {
  fre: "Français",
  eng: "Anglais",
  ger: "Allemand",
  spa: "Espagnol",
  ita: "Italien",
  por: "Portugais",
  rus: "Russe",
  jpn: "Japonais",
  chi: "Chinois",
  ara: "Arabe",
  lat: "Latin",
  grc: "Grec ancien",
};

function getLanguageLabel(code: string | null): string {
  if (!code) return "—";
  return LANGUAGE_LABELS[code.toLowerCase()] || code.toUpperCase();
}

export function WorkDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: work, isLoading, error } = useWork(id);
  const { data: expressions } = useWorkExpressions(id);

  const handleNodeClick = (workId: string) => {
    void navigate(`/catalog/works/${workId}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !work) {
    return (
      <div className="rounded-lg bg-red-100 p-4 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
        Œuvre introuvable ou erreur de chargement.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <Link to="/catalog" className="hover:text-blue-600 dark:hover:text-blue-400">
          Catalogue
        </Link>
        <span>›</span>
        <Link to="/catalog/works" className="hover:text-blue-600 dark:hover:text-blue-400">
          Œuvres
        </Link>
        <span>›</span>
        <span className="truncate text-gray-900 dark:text-white">{work.preferredTitle}</span>
      </nav>

      {/* Hero Section */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="p-6">
          {/* Title and metadata */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {work.preferredTitle}
              </h1>
              {work.variantTitles.length > 0 && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Autres titres : {work.variantTitles.join(" ; ")}
                </p>
              )}
              {work.creatorsDisplay && (
                <p className="mt-2 text-lg text-gray-700 dark:text-gray-300">
                  {work.creatorsDisplay}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Link
                to={`/catalog/works/${work.id}/edit`}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                Modifier
              </Link>
            </div>
          </div>

          {/* Metadata badges */}
          <div className="mt-4 flex flex-wrap gap-2">
            {work.form && (
              <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                {WORK_FORM_LABELS[work.form] || work.form}
              </span>
            )}
            {work.dateOfWorkYear && (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                {work.dateOfWorkYear}
              </span>
            )}
            {work.originalLanguage && (
              <span className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-sm font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                {getLanguageLabel(work.originalLanguage)}
              </span>
            )}
            {work.identifier && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
                {work.identifierSource?.toUpperCase()}
              </span>
            )}
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 divide-x divide-gray-200 border-t border-gray-200 bg-gray-50 dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800/50">
          <div className="px-6 py-4 text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {work.expressionCount}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Expression{work.expressionCount > 1 ? "s" : ""}
            </div>
          </div>
          <div className="px-6 py-4 text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {work.manifestationCount}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Manifestation{work.manifestationCount > 1 ? "s" : ""}
            </div>
          </div>
        </div>
      </div>

      {/* Main content grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left column - Details */}
        <div className="space-y-6 lg:col-span-2">
          {/* Summary */}
          {work.summary && (
            <section className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
              <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Résumé</h2>
              <p className="whitespace-pre-line text-gray-700 dark:text-gray-300">{work.summary}</p>
            </section>
          )}

          {/* Creators */}
          {work.creators.length > 0 && (
            <section className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
              <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                Créateurs
              </h2>
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {work.creators.map((creator: WorkCreator) => (
                  <li key={creator.id} className="flex items-center justify-between py-3">
                    <Link
                      to={`/catalog/authorities/${creator.id}`}
                      className="font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      {creator.name}
                    </Link>
                    <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      {creator.roleLabel}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Interactive Relations Graph */}
          {work.relatedWorks.length > 0 && (
            <section className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
              <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                Réseau des œuvres liées
              </h2>
              <WorkRelationsGraph work={work} onNodeClick={handleNodeClick} />
            </section>
          )}

          {/* Expressions */}
          <section className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Expressions</h2>
              <Link
                to={`/catalog/works/${work.id}/expressions/new`}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Ajouter
              </Link>
            </div>

            {(expressions && expressions.length > 0) || work.expressions.length > 0 ? (
              <div className="space-y-3">
                {(expressions || work.expressions).map((expr: ExpressionSummary) => (
                  <Link
                    key={expr.id}
                    to={`/catalog/expressions/${expr.id}`}
                    className="block rounded-lg border border-gray-200 p-4 transition-colors hover:border-blue-300 hover:bg-blue-50 dark:border-gray-700 dark:hover:border-blue-700 dark:hover:bg-blue-900/20"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="rounded bg-gray-100 px-2 py-1 text-xs font-bold text-gray-700 uppercase dark:bg-gray-700 dark:text-gray-300">
                          {expr.language}
                        </span>
                        {expr.form && (
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {EXPRESSION_FORM_LABELS[expr.form] || expr.form}
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {expr.manifestationCount} manifestation
                        {expr.manifestationCount > 1 ? "s" : ""}
                      </span>
                    </div>
                    {expr.title && expr.title !== work.preferredTitle && (
                      <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{expr.title}</p>
                    )}
                  </Link>
                ))}
              </div>
            ) : (
              <p className="py-8 text-center text-gray-500 dark:text-gray-400">
                Aucune expression enregistrée.
              </p>
            )}
          </section>
        </div>

        {/* Right column - Sidebar */}
        <div className="space-y-6">
          {/* Subjects */}
          {work.subjects.length > 0 && (
            <section className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
              <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Sujets</h2>
              <div className="flex flex-wrap gap-2">
                {work.subjects.map((subject, index) => (
                  <span
                    key={index}
                    className="rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                  >
                    {subject}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Related works */}
          {work.relatedWorks.length > 0 && (
            <section className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
              <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                Œuvres liées
              </h2>
              <ul className="space-y-3">
                {work.relatedWorks.map((related: RelatedWork) => (
                  <li key={related.id}>
                    <Link
                      to={`/catalog/works/${related.id}`}
                      className="group block rounded-lg border border-gray-200 p-3 transition-colors hover:border-blue-300 hover:bg-blue-50 dark:border-gray-700 dark:hover:border-blue-700 dark:hover:bg-blue-900/20"
                    >
                      <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400">
                        {related.title}
                      </p>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {related.relationLabel}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* External links */}
          {work.identifier && (
            <section className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
              <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                Liens externes
              </h2>
              {work.identifierSource === "bnf" && (
                <a
                  href={`https://data.bnf.fr/${work.identifier}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                  Voir sur data.bnf.fr
                </a>
              )}
            </section>
          )}

          {/* Metadata */}
          <section className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Métadonnées
            </h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Créé le</dt>
                <dd className="font-medium text-gray-900 dark:text-white">
                  {new Date(work.createdAt).toLocaleDateString("fr-FR", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Modifié le</dt>
                <dd className="font-medium text-gray-900 dark:text-white">
                  {new Date(work.updatedAt).toLocaleDateString("fr-FR", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">ID</dt>
                <dd className="font-mono text-xs text-gray-600 dark:text-gray-400">{work.id}</dd>
              </div>
            </dl>
          </section>
        </div>
      </div>
    </div>
  );
}
